import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, X, Loader2, Image as ImageIcon, Video } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MediaUploader = ({ 
  onMediaSelect, 
  currentUrl = '', 
  accept = 'image/*,video/*',
  label = 'Media',
  showUrlInput = true 
}) => {
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState('url'); // 'url' or 'upload'
  const [urlValue, setUrlValue] = useState(currentUrl);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/cms/media/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const uploadedUrl = `${BACKEND_URL}${response.data.url}`;
      onMediaSelect(uploadedUrl);
      setUrlValue(uploadedUrl);

      toast({
        title: "Success",
        description: "File uploaded successfully"
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to upload file",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleUrlSubmit = () => {
    if (urlValue && urlValue.trim()) {
      onMediaSelect(urlValue.trim());
      toast({
        title: "Success",
        description: "URL saved"
      });
    }
  };

  const isVideoUrl = (url) => {
    return url && (url.includes('.mp4') || url.includes('.webm') || url.includes('.mov'));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-normal text-[#B5BBC4]">{label}</label>
        {showUrlInput && (
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === 'url' ? 'default' : 'outline'}
              onClick={() => setMode('url')}
              className="text-xs"
            >
              URL
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === 'upload' ? 'default' : 'outline'}
              onClick={() => setMode('upload')}
              className="text-xs"
            >
              Upload
            </Button>
          </div>
        )}
      </div>

      {mode === 'url' && showUrlInput ? (
        <div className="flex gap-2">
          <Input
            type="url"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder="https://example.com/image.jpg"
            className="flex-1"
          />
          <Button
            type="button"
            onClick={handleUrlSubmit}
            className="bg-[#0A1A50] hover:bg-[#060F2E] text-white"
          >
            Save
          </Button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-[#0A1A50] transition-colors">
          <input
            type="file"
            id={`file-upload-${label}`}
            accept={accept}
            onChange={handleFileUpload}
            className="hidden"
            disabled={uploading}
          />
          <label
            htmlFor={`file-upload-${label}`}
            className="flex flex-col items-center justify-center cursor-pointer"
          >
            {uploading ? (
              <Loader2 className="w-12 h-12 text-[#0A1A50] animate-spin mb-2" />
            ) : (
              <Upload className="w-12 h-12 text-[#B5BBC4] mb-2" />
            )}
            <span className="text-sm text-[#B5BBC4] text-center">
              {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
            </span>
            <span className="text-xs text-[#B5BBC4] mt-1">
              {accept === 'image/*' ? 'PNG, JPG, WebP' : 'PNG, JPG, WebP, MP4, WebM'}
            </span>
          </label>
        </div>
      )}

      {/* Preview */}
      {(urlValue || currentUrl) && (
        <div className="relative bg-gray-100 rounded-lg overflow-hidden">
          {isVideoUrl(urlValue || currentUrl) ? (
            <div className="aspect-video">
              <video
                src={urlValue || currentUrl}
                className="w-full h-full object-cover"
                controls
              />
            </div>
          ) : (
            <div className="aspect-video">
              <img
                src={urlValue || currentUrl}
                alt="Preview"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
          <Button
            type="button"
            size="sm"
            variant="destructive"
            className="absolute top-2 right-2"
            onClick={() => {
              setUrlValue('');
              onMediaSelect('');
            }}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
